import React, { memo, Suspense, useCallback, useState, useMemo } from 'react'
import { HStack } from '../../../../../../ui/Stack'
import { Modal } from 'antd'
import { ModalAddNote } from '../../../../../../views/modal-add-notes-new'
import { MontageEditDateModal } from '../../montage-edit-date-modal'
import { ModalCancel } from '../../../../../../views/modal-cancel'
import ModalNotAccepted from '../../modals/not-accepted'
import { getMontageForm } from '../../../../../../store/montage/action-creators'
import { getTimeLineData } from '../../../../../../store/timeline/action-creators'
import { getStagePlanData } from '../../../../../../store/stagePlan/action-creators'
import { useSelector } from 'react-redux'
import { getVisibleFormData, getMontageFormData } from '../../../selectors'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch/useAppDispatch'
import { CardActions, useCardActions, CardActionConfig } from '../../../../../../components/v2/card-actions'
import { EditOutlined, CalendarOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { HSteps } from '../../../../../../ui/Steps'

interface MontageFormsHeaderProps {
  id: string | number,
  className?: string,
  stage?: number,
  isFinished?: boolean
  toOrder?: () => void,
  toClaim?: () => void,
  toFinish?: () => void,
  toNotAccepted?: (data: any) => void,
  showTitle?: boolean,
}

const titlesButton = [
  'Взять в работу',
  'Монтаж произведен',
  'Монтаж принят',
]

export const MontageFormsHeader: React.FC<MontageFormsHeaderProps> = memo((props: MontageFormsHeaderProps) => {
  const {
    className,
    id,
    stage = 0,
    toOrder,
    toFinish,
    isFinished,
    toNotAccepted,
  } = props
  const isDisabledButtons = id === 'new' || stage === 5

  const [isOpenModalAddNote, setIsOpenModalAddNote] = useState<boolean>(false)
  const [isOpenModalMontageEditDate, setIsOpenModalMontageEditDate] = useState<boolean>(false)
  const [isOpenModalCancel, setIsOpenModalCancel] = useState<boolean>(false)
  const [isOpenModalNotAccepted, setIsOpenModalNotAccepted] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const visibleFormData = useSelector(getVisibleFormData)
  const montageFormData = useSelector(getMontageFormData)

  const handleNextStageOnClick = useCallback(async () => {
    if (toOrder) {
      await toOrder?.()
    }
  }, [toOrder])

  const handleNotAcceptedStage = useCallback(async (data: any) => {
    if (toNotAccepted) {
      await toNotAccepted?.(data)
    }
    setIsOpenModalNotAccepted(false)
  }, [toNotAccepted])

  const onCloseAddNote = useCallback(() => {
    setIsOpenModalAddNote(false)
  }, [setIsOpenModalAddNote])

  const onCloseMontageEditDateModal = useCallback(() => {
    setIsOpenModalMontageEditDate(false)
  }, [setIsOpenModalMontageEditDate])

  const handleFinishOnClick = useCallback(async () => {
    if (toFinish) {
      await toFinish?.()
    }
  }, [toFinish])

  const handleCancelOnClick = useCallback(() => {
    if (visibleFormData) {
      const isRequiredFields = isFormDataRequired(visibleFormData?.resultObject, visibleFormData?.resultSchema)
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      }
    }
    setIsOpenModalCancel(true)
  }, [setIsOpenModalCancel, visibleFormData])

  const onCloseCancel = useCallback(() => {
    setIsOpenModalCancel(false)
  }, [setIsOpenModalCancel])

  const onCancel = useCallback(async () => {
    await dispatch(getMontageForm(id))
    dispatch(getTimeLineData('case', id))
    dispatch(getStagePlanData('order', id))
    setIsOpenModalCancel(false)
  }, [id, setIsOpenModalCancel])

  const actionsConfig = useMemo((): CardActionConfig[] => [
    {
      key: 'next-stage',
      label: titlesButton[stage] || '',
      type: 'primary',
      onClick: handleNextStageOnClick,
      isPrimary: true,
      condition: Boolean(titlesButton[stage]),
      order: 1,
    },
    {
      key: 'add-note',
      label: 'Добавить заметку',
      icon: <EditOutlined />,
      onClick: () => setIsOpenModalAddNote(true),
      condition: !isDisabledButtons,
      order: 10,
    },
    {
      key: 'edit-date',
      label: 'Изменить дату',
      icon: <CalendarOutlined />,
      onClick: () => setIsOpenModalMontageEditDate(true),
      condition: stage === 1,
      order: 30,
    },
    {
      key: 'not-accepted',
      label: 'Монтаж не принят',
      onClick: () => setIsOpenModalNotAccepted(true),
      condition: stage === 2,
      order: 35,
    },
    {
      key: 'finish',
      label: 'Выполнено',
      icon: <CheckOutlined />,
      onClick: handleFinishOnClick,
      condition: stage === 3 || stage === 4,
      order: 40,
    },
    {
      key: 'cancel',
      label: 'Отмена',
      icon: <CloseOutlined />,
      danger: true,
      onClick: handleCancelOnClick,
      disabled: stage === 5,
      condition: !isDisabledButtons,
      order: 100,
    },
  ], [stage, isDisabledButtons, handleNextStageOnClick, handleFinishOnClick, handleCancelOnClick])

  const { primaryAction, secondaryActions } = useCardActions({ actions: actionsConfig })

  return (
    <>
      <HStack gap={'8'} className={'mapWrapper'} wrap>
        <div>
          {montageFormData?.stages && (
            <HSteps
              items={Object.values(montageFormData?.stages?.items)}
              current={montageFormData?.stages?.current}
              history={montageFormData?.stages?.history}
            />
          )}
        </div>
        <CardActions
          primaryAction={primaryAction}
          secondaryActions={secondaryActions}
        />
      </HStack>

      <ModalAddNote
        entityId={Number(id)}
        entityType={'case'}
        isOpen={isOpenModalAddNote}
        onClose={onCloseAddNote}
      />

      {stage === 1 && (
        <MontageEditDateModal
          id={id}
          isOpen={isOpenModalMontageEditDate}
          onCancel={onCloseMontageEditDateModal}
        />
      )}

      <Suspense fallback={''}>
        <ModalNotAccepted
          onOk={handleNotAcceptedStage}
          isOpen={isOpenModalNotAccepted}
          onCancel={() => setIsOpenModalNotAccepted(false)}
        />
      </Suspense>

      {!isDisabledButtons && (
        <Suspense fallback={''}>
          <ModalCancel
            entityId={id}
            entityType="montage"
            isOpen={isOpenModalCancel}
            onClose={onCloseCancel}
            onCancel={onCancel}
          />
        </Suspense>
      )}
    </>
  )
})
